import React, { useCallback, useEffect, useRef, useState } from "react";
import { LuX } from "react-icons/lu";
import { colors, typography, effects } from "../../design/tokens";

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ScreenshotCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageDataUrl: string;
  projectId: string;
  onConfirm: (file: File) => void;
}

const MIN_SELECTION_SIZE = 20; // Minimum 20x20px selection

export const ScreenshotCropModal: React.FC<ScreenshotCropModalProps> = ({
  isOpen,
  onClose,
  imageDataUrl,
  projectId,
  onConfirm,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const justFinishedDraggingRef = useRef(false);

  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });
  const [selection, setSelection] = useState<Rect | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageKey, setImageKey] = useState(0); // Cache busting for Safari

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelection(null);
      setIsDragging(false);
      setImageLoaded(false);
      setImageKey((prev) => prev + 1); // Force image remount to bypass Safari cache
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const getRelativePosition = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return { x: 0, y: 0 };

    const rect = containerRef.current.getBoundingClientRect();

    return {
      x: Math.max(0, Math.min(e.clientX - rect.left, rect.width)),
      y: Math.max(0, Math.min(e.clientY - rect.top, rect.height)),
    };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const pos = getRelativePosition(e);
    setStartPos(pos);
    setCurrentPos(pos);
    setIsDragging(true);
    setSelection(null);
  }, [getRelativePosition]);

  // Add global mousemove and mouseup listeners during drag
  // This ensures drag works even when cursor leaves the container
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      // Clamp position to container bounds
      setCurrentPos({
        x: Math.max(0, Math.min(e.clientX - rect.left, rect.width)),
        y: Math.max(0, Math.min(e.clientY - rect.top, rect.height)),
      });
    };

    const handleGlobalMouseUp = () => {
      // Set flag to prevent overlay onClick from closing modal
      justFinishedDraggingRef.current = true;
      requestAnimationFrame(() => {
        justFinishedDraggingRef.current = false;
      });

      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleGlobalMouseMove);
    document.addEventListener("mouseup", handleGlobalMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging]);

  // Calculate selection when drag ends
  useEffect(() => {
    // Only calculate when we just stopped dragging (isDragging went from true to false)
    // and we have valid positions to work with
    if (isDragging) return;

    const x = Math.min(startPos.x, currentPos.x);
    const y = Math.min(startPos.y, currentPos.y);
    const width = Math.abs(currentPos.x - startPos.x);
    const height = Math.abs(currentPos.y - startPos.y);

    // Only set selection if we actually had a meaningful drag (not just initial state)
    if (width >= MIN_SELECTION_SIZE && height >= MIN_SELECTION_SIZE) {
      setSelection({ x, y, width, height });
    }
  }, [isDragging, startPos, currentPos]);

  const cropImage = useCallback(async (rect: Rect | null): Promise<File> => {
    const img = imageRef.current;
    if (!img) throw new Error("Image not loaded");

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get canvas context");

    if (rect && containerRef.current) {
      // Calculate scale factor between displayed size and actual size
      const displayedWidth = containerRef.current.offsetWidth;
      const displayedHeight = containerRef.current.offsetHeight;
      const scaleX = img.naturalWidth / displayedWidth;
      const scaleY = img.naturalHeight / displayedHeight;

      // Set canvas to cropped dimensions
      canvas.width = rect.width * scaleX;
      canvas.height = rect.height * scaleY;

      // Draw cropped region
      ctx.drawImage(
        img,
        rect.x * scaleX,
        rect.y * scaleY,
        rect.width * scaleX,
        rect.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
      );
    } else {
      // Full screenshot
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);
    }

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to create blob"));
      }, "image/png");
    });

    const filename = rect
      ? `${projectId}-screenshot-selection.png`
      : `${projectId}-screenshot.png`;

    return new File([blob], filename, { type: "image/png" });
  }, [projectId]);

  const handleUseFullScreenshot = useCallback(async () => {
    try {
      const file = await cropImage(null);
      onConfirm(file);
      onClose();
    } catch (error) {
      console.error("Failed to process screenshot:", error);
    }
  }, [cropImage, onConfirm, onClose]);

  const handleUseSelection = useCallback(async () => {
    if (!selection) return;
    try {
      const file = await cropImage(selection);
      onConfirm(file);
      onClose();
    } catch (error) {
      console.error("Failed to crop screenshot:", error);
    }
  }, [selection, cropImage, onConfirm, onClose]);

  const handleClearSelection = useCallback(() => {
    setSelection(null);
  }, []);

  // Calculate current drag rectangle for display
  const dragRect = isDragging ? {
    x: Math.min(startPos.x, currentPos.x),
    y: Math.min(startPos.y, currentPos.y),
    width: Math.abs(currentPos.x - startPos.x),
    height: Math.abs(currentPos.y - startPos.y),
  } : null;

  const displayRect = selection || dragRect;

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${colors.overlay.medium} backdrop-blur-sm`}
      onClick={(e) => {
        if (e.target === e.currentTarget && !justFinishedDraggingRef.current) onClose();
      }}
    >
      <div className={`${colors.surface.primary} ${effects.borderRadius.xl} ${colors.border.primary} border ${effects.shadow.xl} flex flex-col overflow-hidden max-w-[90vw] max-h-[85vh]`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${colors.border.primary}`}>
          <h2 className={`${typography.size.lg} ${typography.weight.semibold} ${colors.text.primary}`}>
            Screenshot
          </h2>
          <button
            onClick={onClose}
            className={`${colors.text.secondary} hover:text-dark-text ${effects.transition.default} p-1`}
          >
            <LuX size={20} />
          </button>
        </div>

        {/* Image container */}
        <div className="relative flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="px-6 pt-6 flex-1 flex items-center justify-center overflow-hidden">
            <div
              ref={containerRef}
              className="relative select-none cursor-crosshair isolate"
              style={{ maxHeight: '100%', maxWidth: '100%' }}
              onMouseDown={handleMouseDown}
            >
              <img
                key={imageKey}
                ref={imageRef}
                src={imageDataUrl}
                alt="Screenshot preview"
                className={`${effects.borderRadius.lg} block`}
                style={{ maxHeight: '60vh', width: 'auto' }}
                onLoad={() => setImageLoaded(true)}
                draggable={false}
              />

              {/* Selection overlay - darkens non-selected area */}
              {/* Note: Using inline backgroundColor instead of Tailwind's bg-black/50 for Brave browser compatibility */}
              {imageLoaded && displayRect && (
                <>
                  {/* Top overlay */}
                  <div
                    className="absolute z-10 pointer-events-none"
                    style={{
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: displayRect.y,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    }}
                  />
                  {/* Bottom overlay */}
                  <div
                    className="absolute z-10 pointer-events-none"
                    style={{
                      top: displayRect.y + displayRect.height,
                      left: 0,
                      width: '100%',
                      height: `calc(100% - ${displayRect.y + displayRect.height}px)`,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    }}
                  />
                  {/* Left overlay */}
                  <div
                    className="absolute z-10 pointer-events-none"
                    style={{
                      top: displayRect.y,
                      left: 0,
                      width: displayRect.x,
                      height: displayRect.height,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    }}
                  />
                  {/* Right overlay */}
                  <div
                    className="absolute z-10 pointer-events-none"
                    style={{
                      top: displayRect.y,
                      left: displayRect.x + displayRect.width,
                      width: `calc(100% - ${displayRect.x + displayRect.width}px)`,
                      height: displayRect.height,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    }}
                  />

                  {/* Selection border */}
                  <div
                    className="absolute z-20 border-2 border-dashed border-white pointer-events-none"
                    style={{
                      left: displayRect.x,
                      top: displayRect.y,
                      width: displayRect.width,
                      height: displayRect.height,
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Hint text */}
        <p className={`${typography.size.sm} ${colors.text.secondary} text-center px-6 py-3`}>
          {selection
            ? "Selection ready. Click 'Use selection' to attach."
            : "Drag to select an area, or use full screenshot."
          }
        </p>

        {/* Footer with buttons */}
        <div className={`flex items-center justify-center gap-3 px-6 py-4 border-t ${colors.border.primary}`}>
          {selection ? (
            /* Selection made - show Clear and Use Selection */
            <>
              <button
                onClick={handleClearSelection}
                className={`px-6 py-2.5 ${effects.borderRadius.lg} ${effects.transition.default}
                  ${colors.surface.secondary} ${colors.text.primary} ${colors.hover.backgroundAlt} border ${colors.border.primary}
                  ${typography.size.sm} ${typography.weight.medium}`}
              >
                Clear selection
              </button>
              <button
                onClick={handleUseSelection}
                className={`px-6 py-2.5 ${effects.borderRadius.lg} ${effects.transition.default}
                  bg-primary-blue text-white ${colors.hover.opacity}
                  ${typography.size.sm} ${typography.weight.medium}`}
              >
                Use selection
              </button>
            </>
          ) : (
            /* No selection - show Use full screenshot */
            <button
              onClick={handleUseFullScreenshot}
              className={`px-6 py-2.5 ${effects.borderRadius.lg} ${effects.transition.default}
                bg-primary-blue text-white ${colors.hover.opacity}
                ${typography.size.sm} ${typography.weight.medium}`}
            >
              Use full screenshot
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
